<div class="lane-management-card">
  <div class="card-header">
    <div class="card-header-text">
      <h2 class="card-title">Tunnel</h2>
      <p class="card-description">
        The Tunnel Controller starts the tunnel wash for each car: it pulses the c-relay of the
        car's tunnel wash package.
      </p>
    </div>
    @if (!loading && !loadFailed && !tunnel) {
      <app-add-button buttonText="Add tunnel" variant="primary" (buttonClick)="onAddOrEditTunnel()">
      </app-add-button>
    }
  </div>

  @if (loading && !tunnel) {
    <div class="loading-container">
      <app-loading-table-shimmer
        [columns]="packageShimmerColumns"
        [rows]="2"
        [showHeader]="false"
        className="scrollable"
      >
      </app-loading-table-shimmer>
    </div>
  } @else if (loadFailed) {
    <div class="empty-state-container">
      <app-empty-result
        image="/assets/images/empty_screen.png"
        title="Failed to load tunnel settings"
        description="Something went wrong while loading the tunnel of this location."
        actionText="Retry"
        [showAction]="true"
        (actionClicked)="load()"
      >
      </app-empty-result>
    </div>
  } @else if (tunnel) {
    <div class="tunnel-summary">
      <dl class="tunnel-fields">
        <div class="tunnel-field">
          <dt>Name</dt>
          <dd class="tunnel-name">{{ tunnel.name }}</dd>
        </div>
        <div class="tunnel-field">
          <dt>Device serial</dt>
          <dd>
            <div class="device-cell">
              <span
                class="connection-dot"
                [class.last-seen]="connection.state === 'last_seen'"
                [class.disconnected]="connection.state === 'never'"
                aria-hidden="true"
              ></span>
              <span class="device-text">{{ tunnel.serial }}</span>
            </div>
            <span class="tunnel-connection" [class.never]="connection.state === 'never'">{{
              connection.label
            }}</span>
          </dd>
        </div>
        <div class="tunnel-field tunnel-field--wide">
          <dt>Starts the wash</dt>
          <dd>{{ triggerModeLabel }}</dd>
        </div>
      </dl>
      <div class="actions-cell tunnel-actions">
        <button
          type="button"
          class="action-btn"
          (click)="onAddOrEditTunnel()"
          aria-label="Edit tunnel"
        >
          <img src="/assets/images/edit-icon.svg" alt="" width="18" height="18" />
        </button>
        <button
          type="button"
          class="action-btn"
          (click)="onDeleteTunnel()"
          aria-label="Delete tunnel"
        >
          <img src="/assets/images/trash.svg" alt="" width="18" height="18" />
        </button>
      </div>
    </div>
  } @else {
    <div class="empty-state-container">
      <app-empty-result
        image="/assets/images/empty_screen.png"
        title="No tunnel configured"
        description="Add the tunnel and the device its Tunnel Controller runs on. Until then every wash is started by hand."
        [actionText]="''"
        [showAction]="false"
      >
      </app-empty-result>
    </div>
  }
</div>

@if (tunnel) {
  <div class="lane-management-card">
    <div class="card-header">
      <div class="card-header-text">
        <h2 class="card-title">Wash packages of {{ tunnel.name }}</h2>
        <p class="card-description">
          One per wash program of this tunnel's controller, with the c-relay that starts it.
        </p>
      </div>
      <app-add-button buttonText="Add package" variant="primary" (buttonClick)="onAddPackage()">
      </app-add-button>
    </div>

    @if (tunnelPackages.length > 0) {
      <div class="table-container">
        <table class="lanes-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>C-relay</th>
              <th>Status</th>
              <th>Packages</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            @for (pkg of tunnelPackages; track pkg.id) {
              <tr>
                <td class="lane-name">{{ pkg.name }}</td>
                <td>{{ pkg.relay }}</td>
                <td>
                  <span class="status-badge" [class.active]="pkg.status === 'active'">
                    {{ pkg.status === 'active' ? 'Active' : 'Inactive' }}
                  </span>
                </td>
                <td>{{ pkg.package_count }}</td>
                <td class="actions-cell">
                  <button
                    type="button"
                    class="action-btn"
                    (click)="onEditPackage(pkg)"
                    [attr.aria-label]="'Edit tunnel wash package ' + pkg.name"
                  >
                    <img src="/assets/images/edit-icon.svg" alt="" width="18" height="18" />
                  </button>
                  <button
                    type="button"
                    class="action-btn"
                    (click)="onDeletePackage(pkg)"
                    [attr.aria-label]="'Delete tunnel wash package ' + pkg.name"
                  >
                    <img src="/assets/images/trash.svg" alt="" width="18" height="18" />
                  </button>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    } @else {
      <div class="empty-state-container">
        <app-empty-result
          image="/assets/images/empty_screen.png"
          title="No tunnel wash packages"
          description="Add the tunnel's wash programs. Each package then picks its tunnel wash package in the shop's package form."
          [actionText]="''"
          [showAction]="false"
        >
        </app-empty-result>
      </div>
    }
  </div>
}

<app-tunnel-form-modal
  [isOpen]="showTunnelForm"
  [companyId]="companyId"
  [locationId]="locationId"
  [tunnel]="tunnel"
  (close)="onCloseTunnelForm()"
  (saved)="onTunnelSaved()"
>
</app-tunnel-form-modal>

<app-tunnel-package-form-modal
  [isOpen]="showPackageForm"
  [companyId]="companyId"
  [locationId]="locationId"
  [tunnelPackage]="editingPackage"
  [existingPackages]="tunnelPackages"
  (close)="onClosePackageForm()"
  (saved)="onPackageSaved()"
>
</app-tunnel-package-form-modal>

@if (showDeleteTunnel && tunnel) {
  <app-confirm-action-modal
    testId="delete-tunnel-dialog"
    title="Delete tunnel?"
    confirmLabel="Delete"
    busyLabel="Deleting…"
    [busy]="isDeletingTunnel"
    [mobileSheet]="false"
    [message]="tunnelDeleteMessage"
    (confirm)="onConfirmDeleteTunnel()"
    (cancel)="onCancelDeleteTunnel()"
  >
  </app-confirm-action-modal>
}

@if (packageToDelete) {
  <app-confirm-action-modal
    testId="delete-tunnel-package-dialog"
    title="Delete tunnel wash package?"
    confirmLabel="Delete"
    busyLabel="Deleting…"
    [busy]="isDeletingPackage"
    [mobileSheet]="false"
    [message]="packageDeleteMessage"
    (confirm)="onConfirmDeletePackage()"
    (cancel)="onCancelDeletePackage()"
  >
  </app-confirm-action-modal>
}
